import { alpha, type Components, type Theme } from '@mui/material';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { type AlertTone, alertTones, darkShadows, fontFamilies, lavender, radii, softShadows, status } from './tokens';

/**
 * Product-shaped overrides: soft corners, white panels on a neutral ground,
 * roomy controls. Controls 6px, chips and menus 8, alerts 10, panels 12.
 *
 * Nothing moves (owner, 2026-10-21): a hover, a focus, a selection and an
 * opening menu all appear in their final state on the first frame. The theme's
 * `transitions` are zeroed in `theme/index.ts`, the ripple is off on every
 * ButtonBase, and the baseline carries a CSS backstop. The exceptions are the
 * logo family (`UruannaMark`, the boot splash) and the loading signals — the
 * skeleton pulse and the aqua bar.
 *
 * Selected is one look everywhere — a sidebar item, a menu item, a list row,
 * the current page: the neutral fill, bold ink, and the icon in the primary.
 * Never a tinted pill, never a coloured side-stripe (owner's rule).
 *
 * Colours come from `theme.palette.brand` — the mode's token set — so this file
 * never names a hex.
 */
/** Menus and popovers: an ink shadow on light, a darkening on black. */
export const popoverShadow = (theme: Theme) =>
  theme.palette.mode === 'dark' ? darkShadows.popover : softShadows.popover;
/** Drawers and dialogs. */
export const overlayShadow = (theme: Theme) =>
  theme.palette.mode === 'dark' ? darkShadows.overlay : softShadows.overlay;

/**
 * Text inside inputs is always vertically centred (owner's rule, 2026-09-17),
 * and it is enforced here, never per component.
 *
 * The geometry is fixed in pixels so every part of a field lands on the same
 * midline: the value, the placeholder, the resting label, an adornment icon,
 * the select arrow. MUI sizes the input as `1.4375em`, which at the 15px body
 * size is 21.5625px — a box 42.56px tall whose top border is painted across
 * two pixel rows and whose centre is a quarter-pixel from anything else on the
 * row. Whole numbers keep the border crisp and the midline exact:
 *
 *   medium  22px line + 2 × 10px = 42px    small  20px line + 2 × 8px = 36px
 *
 * The label is **above** the box, never inside it (Components, Overlays and
 * dark pass boards, redesign phase 1): 13px/500 in the ink, 6px over the
 * field, in the document flow. So nothing floats, there is no notch cut in
 * the top border, and the focus halo — 1px primary border plus the 3px aqua
 * ring every other control wears — cannot cross the label, which is the
 * collision the notched model existed to avoid. The box itself keeps the
 * numbers above; the label adds its own line over it. A textarea keeps the
 * same numbers so its first line sits exactly where a single-line value
 * would. Inline padding is 12 (the boards).
 */
export const INPUT_GEOMETRY = {
  medium: { line: 22, paddingBlock: 10 },
  small: { line: 20, paddingBlock: 8 },
  paddingInline: 12,
} as const;

/** The label's own line over a field: 13px on a 20px line, 6px above the box. */
export const FIELD_LABEL = { fontSize: 13, line: 20, gap: 6 } as const;

/**
 * Chrome that must never join a text selection. Spelled with the `-webkit-`
 * prefix as well, because Safari still needs it.
 */
export const NOT_SELECTABLE = {
  WebkitUserSelect: 'none',
  userSelect: 'none',
} as const;

/**
 * A field whose value is always left-to-right — a username, a phone, a code —
 * carries `dir="ltr"` on its FormControl, so the value and the caret run the
 * right way in Arabic. Its chrome is not the value. The label is placed by
 * physical properties the RTL cache has already flipped to the right, but the
 * notch is the `<legend>` of a `<fieldset>` and sits at the fieldset's
 * *inline start*, which `dir="ltr"` made the left: the gap opened on the
 * opposite side from the label above it. The helper line had the same fault
 * in its punctuation. So the label, the notch and the helper text take the
 * page's direction back; only the input row stays LTR.
 *
 * A variable, not `direction: 'rtl'`: the RTL cache runs every declaration
 * through cssjanus, which swaps `direction: ltr` and `direction: rtl`, so a
 * literal would be written as its own opposite. `var(--page-dir)` is set from
 * the document's `dir` in the baseline and passes through untouched.
 */
const LTR_FIELD_CHROME = { direction: 'var(--page-dir)' } as const;

/** MUI's alert severities onto the brand's four alert tones. */
const ALERT_TONES = {
  success: 'success',
  warning: 'warning',
  error: 'danger',
  info: 'info',
} as const satisfies Record<string, AlertTone>;

export function buildComponents(): Components<Omit<Theme, 'components'>> {
  const { medium, small, paddingInline } = INPUT_GEOMETRY;
  const focusRing = (theme: Theme) => ({ boxShadow: `0 0 0 3px ${theme.palette.brand.focusRing}` });

  return {
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        html: { WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' },
        body: { overflowX: 'hidden', backgroundColor: theme.palette.background.default },
        '::selection': {
          background: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        },
        '*::-webkit-scrollbar': { width: 12, height: 12 },
        '*::-webkit-scrollbar-thumb': {
          borderRadius: radii.pill,
          border: '3px solid transparent',
          backgroundClip: 'content-box',
          backgroundColor: theme.palette.brand.borderStrong,
        },
        '.mono': { fontFamily: fontFamilies.mono, fontVariantNumeric: 'tabular-nums' },
        '.tabular': { fontVariantNumeric: 'tabular-nums' },
        // An icon that implies a direction (a back arrow, a "go" chevron) is
        // drawn with `transform: scaleX(var(--dir-scale))` and flips with the
        // document, not with a per-component check. `--page-dir` is the same
        // fact as a `direction` value; see `LTR_FIELD_CHROME` for why it is a
        // variable and not a literal.
        ':root': { '--dir-scale': '1', '--page-dir': 'ltr' },
        ':root[dir="rtl"]': { '--dir-scale': '-1', '--page-dir': 'rtl' },
        // Toasts: the inverse surface in both modes, the meaning carried by the
        // icon's colour — never a coloured side-stripe (owner's rule). The
        // surface is dark in both modes, so the icons take the dark tones.
        '.notistack-MuiContent': {
          backgroundColor: `${theme.palette.brand.inverseSurface} !important`,
          color: `${theme.palette.brand.inverseInk} !important`,
          borderRadius: radii.lg,
          boxShadow:
            theme.palette.mode === 'dark' ? darkShadows.popover : softShadows.toast,
          border: `1px solid ${theme.palette.brand.inverseBorder}`,
          fontFamily: theme.typography.fontFamily,
          fontSize: '0.875rem',
          fontWeight: 400,
          padding: '4px 14px',
          gap: 12,
        },
        '.notistack-MuiContent #notistack-snackbar': { gap: 12, paddingBlock: 8 },
        '.notistack-MuiContent #notistack-snackbar > svg': { marginInlineEnd: '0 !important', fontSize: 20 },
        '.notistack-MuiContent > div:last-of-type:not(#notistack-snackbar)': {
          marginInlineStart: 'auto',
          paddingInlineStart: 0,
          marginInlineEnd: 0,
        },
        // An inline action (Overlays board): a text button is a link-weight
        // word in the light lavender ("View"); any other variant is a quiet
        // filled chip on the inverse surface ("Retry").
        '.notistack-MuiContent .MuiButton-root': {
          minHeight: 30,
          fontSize: '0.8125rem',
          fontWeight: 600,
          borderRadius: radii.smallControl,
        },
        '.notistack-MuiContent .MuiButton-text': {
          color: `${theme.palette.brand.inverseAction} !important`,
          paddingInline: 0,
          minWidth: 0,
          fontSize: '0.875rem',
          textDecoration: 'underline',
          textUnderlineOffset: 3,
          backgroundColor: 'transparent !important',
        },
        '.notistack-MuiContent .MuiButton-contained, .notistack-MuiContent .MuiButton-outlined': {
          color: `${theme.palette.brand.inverseInk} !important`,
          backgroundColor: 'rgba(255, 255, 255, 0.12) !important',
          border: 0,
          paddingInline: 10,
        },
        '.notistack-MuiContent svg': { color: 'inherit' },
        '.notistack-MuiContent-success svg': { color: theme.palette.brand.signal },
        '.notistack-MuiContent-error svg': { color: status.danger.dark },
        '.notistack-MuiContent-warning svg': { color: status.warning.dark },
        '.notistack-MuiContent-info svg': { color: theme.palette.brand.inverseAction },
        // The backstop for "nothing moves": a transition written outside the
        // theme — notistack's slide and collapse set theirs inline — still
        // lands in its end state at once. Keyframe animations are untouched,
        // because the logo, the loading signals and the page enter are the
        // ones allowed.
        '*, *::before, *::after': {
          boxSizing: 'border-box',
          transitionDuration: '0s !important',
          transitionDelay: '0s !important',
        },
        // A reader who asks for less motion gets none at all, the logo and
        // the loading signals included.
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      }),
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none', borderRadius: radii.xl } },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:not(.MuiBackdrop-invisible)': { backgroundColor: theme.palette.brand.scrim },
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: radii.xl,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
        }),
      },
    },
    MuiCardHeader: {
      defaultProps: { titleTypographyProps: { variant: 'h5' } },
      styleOverrides: {
        root: { padding: '12px 16px' },
        action: { margin: 0, alignSelf: 'center' },
      },
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: 16, '&:last-child': { paddingBottom: 16 } } },
    },
    MuiButtonBase: {
      // No ripple anywhere: a press is answered by the pressed colour, not by
      // an expanding circle.
      defaultProps: { disableRipple: true, disableTouchRipple: true },
      styleOverrides: {
        // Keyboard focus is a ring, everywhere, and never the default outline.
        root: ({ theme }) => ({ '&.Mui-focusVisible': focusRing(theme) }),
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        // Components board: 15px, 600 for every act and 500 for the quiet
        // secondary; 18px of inline padding (14 on a text button).
        root: ({ theme }) => ({
          borderRadius: radii.sm,
          paddingInline: 18,
          fontSize: '0.9375rem',
          fontWeight: 600,
          // The control height (brief): a button beside a 42px input in a
          // toolbar or a form footer stands on the same line.
          minHeight: 42,
          '&.Mui-focusVisible': focusRing(theme),
        }),
        sizeSmall: { minHeight: 34, paddingInline: 12, fontSize: '0.8125rem', borderRadius: radii.smallControl },
        sizeLarge: { minHeight: 42, paddingInline: 20, fontSize: '0.9375rem' },
        contained: ({ theme }) => ({
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          // A disabled act keeps its shape and reads as a button that is
          // waiting, not as a grey blot: a quiet grey surface, the label
          // still legible (#ECECEF / #8C8A9C, dark #232326 / #8C8C93).
          '&.Mui-disabled': {
            backgroundColor: theme.palette.brand.disabledSurface,
            color: theme.palette.brand.disabledInk,
          },
        }),
        containedPrimary: ({ theme }) => ({
          '&:hover': { backgroundColor: theme.palette.brand.primaryHover },
        }),
        // Approve: the teal act, one of the review's three named buttons.
        containedSuccess: ({ theme }) => ({
          backgroundColor: theme.palette.brand.approve,
          color: theme.palette.brand.onApprove,
          '&:hover': { backgroundColor: alpha(theme.palette.brand.approve, 0.88) },
        }),
        containedError: ({ theme }) => ({
          '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.88) },
        }),
        outlined: ({ theme }) => ({
          fontWeight: 500,
          borderColor: theme.palette.brand.borderStrong,
          // Light: a white button on the panel. Dark: the panel shows through.
          backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : theme.palette.background.paper,
          '&:hover': {
            borderColor: theme.palette.text.disabled,
            backgroundColor: theme.palette.action.hover,
          },
          '&.Mui-disabled': {
            borderColor: theme.palette.divider,
            color: theme.palette.brand.disabledInk,
          },
        }),
        outlinedInherit: ({ theme }) => ({ color: theme.palette.text.primary }),
        // Destructive, outlined: the danger word on the panel inside a pale
        // danger edge — "Delete", "Reject" beside a neutral "Request changes".
        outlinedError: ({ theme }) => ({
          fontWeight: 600,
          color: theme.palette.error.main,
          borderColor: theme.palette.brand.dangerBorder,
          '&:hover': {
            borderColor: theme.palette.error.main,
            backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
          },
        }),
        text: ({ theme }) => ({
          paddingInline: 14,
          '&.Mui-disabled': { color: theme.palette.brand.disabledInk },
        }),
        textPrimary: ({ theme }) => ({ color: theme.palette.brand.primaryInk }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: radii.sm,
          '&.Mui-focusVisible': focusRing(theme),
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: { root: ({ theme }) => ({ '&.Mui-focusVisible': focusRing(theme) }) },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: radii.md,
          backgroundColor: theme.palette.brand.well,
          padding: 3,
          gap: 2,
        }),
        grouped: { border: 0, borderRadius: `${radii.sm}px !important` },
      },
    },
    // A segmented control (Components board): 32px segments, 13px, in a 3px
    // well; `large` is the form-row variant that stands 42 tall beside an
    // input (Overlays board's gender choice).
    MuiToggleButton: {
      styleOverrides: {
        sizeMedium: { minHeight: 32, fontSize: '0.8125rem' },
        sizeLarge: { minHeight: 36, fontSize: '0.875rem', paddingBlock: 0 },
        root: ({ theme }) => ({
          textTransform: 'none',
          fontWeight: 400,
          padding: '4px 12px',
          border: 0,
          color: theme.palette.text.secondary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.primary,
          },
          // A segmented control: the chosen segment is the raised panel with
          // bold ink — the same neutral language as every other selection.
          '&.Mui-selected': {
            color: theme.palette.text.primary,
            fontWeight: 600,
            backgroundColor: theme.palette.background.paper,
            boxShadow:
              theme.palette.mode === 'dark'
                ? `inset 0 0 0 1px ${theme.palette.brand.borderStrong}`
                : softShadows.segment,
          },
          '&.Mui-selected:hover': { backgroundColor: theme.palette.background.paper },
          '&.Mui-disabled': { color: theme.palette.brand.disabledInk, border: 0 },
        }),
      },
    },
    // Checkbox and radio: the primary when chosen, a faint ink ring when not
    // (still 3:1 against the panel, unlike a border grey), 15px captions.
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: 6,
          color: theme.palette.text.disabled,
          '&.Mui-disabled': { color: theme.palette.brand.borderStrong },
        }),
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: 6,
          color: theme.palette.text.disabled,
          '&.Mui-disabled': { color: theme.palette.brand.borderStrong },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radii.md, fontWeight: 550, height: 24 },
        sizeSmall: { height: 21, fontSize: '0.75rem' },
        label: { paddingInline: 8 },
        outlined: ({ theme }) => ({ borderColor: theme.palette.brand.borderStrong }),
        deleteIcon: ({ theme }) => ({
          color: theme.palette.text.disabled,
          '&:hover': { color: theme.palette.text.primary },
        }),
      },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.brand.primaryInk,
          fontWeight: 500,
          '&:hover': { color: theme.palette.mode === 'light' ? theme.palette.brand.primary : lavender[100] },
        }),
      },
    },
    MuiTextField: { defaultProps: { size: 'medium', fullWidth: true } },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({ color: theme.palette.text.primary, alignItems: 'center' }),
      },
    },
    /*
     * Focus on a field is the product's one focus look: a 1px `primary`
     * border and the 3px aqua ring (Components and dark pass boards).
     *
     * The ring used to be forbidden here. With the label floating in a notch
     * cut into the top border, a box-shadow — which cannot be notched — ran
     * straight through the gap and across the label's glyphs; the owner
     * reported it twice ("the label sits ON the top border", "the outline
     * reads as a doubled line"). The label is now above the box and there is
     * no notch at all (the `<legend>` is not drawn), so the ring has nothing
     * to cross. A rejected field keeps its danger border while focused, so
     * focusing it does not hide the rejection; the ring says where the caret
     * is, the border says what is wrong.
     */
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: radii.sm,
          backgroundColor: theme.palette.background.paper,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.brand.borderStrong },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.text.disabled,
          },
          '&.Mui-focused': focusRing(theme),
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 1,
            borderColor: theme.palette.primary.main,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline, &.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline':
            { borderWidth: 1, borderColor: theme.palette.error.main },
          // Disabled and locked (Components, Errors boards): the field sinks
          // to the ground's grey, its edge to a hairline, its value to the
          // disabled ink — and MUI's own fill colour has to be overridden for
          // Safari, which paints a disabled value with -webkit-text-fill-color.
          '&.Mui-disabled': { backgroundColor: theme.palette.brand.disabledField },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
          '&.Mui-disabled .MuiOutlinedInput-input, &.Mui-disabled .MuiSelect-select': {
            color: theme.palette.brand.disabledInk,
            WebkitTextFillColor: theme.palette.brand.disabledInk,
          },
          '&.Mui-disabled .MuiInputAdornment-root': { color: theme.palette.brand.disabledInk },
        }),
        // No notch: the label is above the box, so the `<legend>` that would
        // cut a gap for it is not drawn, and the fieldset sits on the box's
        // own edge rather than 5px above it. The legend also carried a second,
        // invisible copy of the label text; with it gone a drag cannot select it.
        notchedOutline: {
          ...NOT_SELECTABLE,
          top: 0,
          paddingInline: 0,
          '& legend': { display: 'none' },
        },
        // Never write a bare `::placeholder` here. The RTL cache runs the
        // `prefixer` from the top-level `stylis` (4.4) inside Emotion's own
        // stylis (4.2), and that prefixer's placeholder expansion crashes on
        // the older parse tree — the whole page goes blank. The engine
        // selectors, spelled out as MUI does, pass straight through. MUI's own
        // `!important` still hides the placeholder under a resting label.
        //
        // A `type=number` field carries the browser's spinner, and the spinner
        // is laid out *inside* the value box at the inline end: it eats ~16px
        // of the field whether or not it is painted (Chrome only paints it on
        // hover and focus), which in RTL leaves a block of dead space at the
        // start of an Arabic field — the "white space" in the owner's report.
        // It is removed here, for every number field in the product, and the
        // value keeps the full 14px inline padding. Nothing is lost: the up
        // and down arrow keys still step the value, and the wheel no longer
        // silently changes a quantity the pointer merely passed over.
        input: ({ theme }) => ({
          height: medium.line,
          lineHeight: `${medium.line}px`,
          paddingBlock: medium.paddingBlock,
          paddingInline,
          '&::-webkit-input-placeholder': { color: theme.palette.text.disabled, opacity: 1 },
          '&::-moz-placeholder': { color: theme.palette.text.disabled, opacity: 1 },
          // A left-to-right value (username, licence number) in an Arabic
          // form keeps its own direction but sits where the reader's eye
          // starts, at the field's right edge (Components board). `end` is
          // that edge for a dir="ltr" input, and the RTL cache does not flip
          // it. A box that is itself LTR (the phone) is left alone.
          'html[dir="rtl"] .MuiInputBase-root:not([dir="ltr"]) &[dir="ltr"]': { textAlign: 'end' },
          '&[type=number]': { MozAppearance: 'textfield' },
          '&[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
          '&[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
        }),
        inputSizeSmall: {
          height: small.line,
          lineHeight: `${small.line}px`,
          paddingBlock: small.paddingBlock,
        },
        // A textarea pads its root, not its input; same numbers, so the first
        // line sits exactly where a single-line value would.
        multiline: {
          paddingBlock: medium.paddingBlock,
          paddingInline,
          '&.MuiInputBase-sizeSmall': { paddingBlock: small.paddingBlock },
          '& textarea': { lineHeight: `${medium.line}px` },
          '&.MuiInputBase-sizeSmall textarea': { lineHeight: `${small.line}px` },
        },
        // The root carries the inline padding for a textarea; the textarea
        // itself carries none, or the first line starts 24px in.
        inputMultiline: { height: 'auto', paddingBlock: 0, paddingInline: 0 },
        inputAdornedStart: { paddingInlineStart: 0 },
        inputAdornedEnd: { paddingInlineEnd: 0 },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        // The grid footer's page-size select is a bare InputBase; it gets the
        // small line box and no block padding, centred by the footer row.
        select: { minHeight: small.line, lineHeight: `${small.line}px`, paddingBlock: 4 },
        selectIcon: { top: '50%', transform: 'translateY(-50%)' },
        selectLabel: { margin: 0 },
        displayedRows: { margin: 0 },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        // MUI gives the adornment a 0.01em height inside a centred flex row;
        // the icon inside it is a block with no line box of its own, so both
        // end up on the row's midline. Stated explicitly so nothing upstream
        // (a `dir`, a font swap) can move them.
        //
        // Every adornment icon is the DataTable search icon's size — 20px,
        // muted — including the ones inside a button (the picker's calendar,
        // the password eye, the search clear). MUI's IconButton is 40px with
        // a 24px glyph, which dominated a 42px field; it is trimmed to a 32px
        // target here, and the adornment's fixed height keeps the field at
        // 42/36 whatever sits inside it.
        root: ({ theme }) => ({
          height: medium.line,
          maxHeight: medium.line,
          alignSelf: 'center',
          color: theme.palette.text.disabled,
          '& .MuiSvgIcon-root': { display: 'block', fontSize: '1.25rem' },
          '& .MuiIconButton-root': { margin: 0, padding: 6, color: theme.palette.text.disabled },
          '& .MuiIconButton-root:hover': { color: theme.palette.text.primary },
          // `sizeSmall` is not an override slot on InputAdornment (MUI's
          // resolver only maps root, position and variant), hence nested.
          '&.MuiInputAdornment-sizeSmall': {
            height: small.line,
            maxHeight: small.line,
            '& .MuiIconButton-root': { padding: 4 },
          },
        }),
        positionStart: { marginInlineEnd: 8 },
        positionEnd: { marginInlineStart: 8 },
      },
    },
    MuiSelect: {
      styleOverrides: {
        // The select is a div, not an input, and MUI gives it only a
        // `min-height`; a flex row with the same line box puts the chosen
        // value — text or a chip — on the midline.
        select: {
          display: 'flex',
          alignItems: 'center',
          minHeight: medium.line,
          lineHeight: `${medium.line}px`,
          height: 'auto',
          '&.MuiInputBase-inputSizeSmall': { minHeight: small.line, lineHeight: `${small.line}px` },
          '&.MuiTablePagination-select': { minHeight: small.line, lineHeight: `${small.line}px` },
        },
        icon: ({ theme }) => ({
          top: '50%',
          transform: 'translateY(-50%)',
          color: theme.palette.text.disabled,
        }),
      },
    },
    MuiNativeSelect: {
      styleOverrides: { select: { height: medium.line, lineHeight: `${medium.line}px` } },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          paddingBlock: `${medium.paddingBlock}px !important`,
          '& .MuiAutocomplete-input': { paddingBlock: '0 !important', height: medium.line },
          '&.MuiInputBase-sizeSmall': {
            paddingBlock: `${small.paddingBlock}px !important`,
            '& .MuiAutocomplete-input': { height: small.line },
          },
        },
        endAdornment: { top: '50%', transform: 'translateY(-50%)' },
        /*
         * A group heading now names the institution behind a register — "Listed
         * in the Iraqi Pharmacists Syndicate register of pharmacies" — which
         * wraps in a narrow column. MUI's own `groupLabel` is a 48px line box,
         * so a wrapped heading prints two lines 48px apart with the second
         * stranded half a row below. Set it to a real line-height and pad the
         * block instead, once, for every grouped list in the product.
         */
        groupLabel: { lineHeight: 1.45, paddingBlock: 8 },
        popupIndicator: ({ theme }) => ({ color: theme.palette.text.disabled }),
        paper: ({ theme }) => ({
          borderRadius: radii.md,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: popoverShadow(theme),
        }),
      },
    },
    /*
     * The label is a line of its own above the box (Components board): 13px,
     * 500, in the ink; red with the field when it is rejected; the disabled
     * ink when the field is. It never moves — `shrink` is on by default so
     * MUI neither parks it inside the box nor hides the placeholder under it
     * — and it is in the document flow, so the FormControl's column makes room
     * for it instead of the field overlapping whatever sits above.
     */
    MuiInputLabel: {
      defaultProps: { shrink: true },
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: `${FIELD_LABEL.fontSize / 16}rem`,
          lineHeight: `${FIELD_LABEL.line}px`,
          fontWeight: 500,
          color: theme.palette.text.primary,
          '&.Mui-focused': { color: theme.palette.text.primary },
          // A rejected field stays rejected while the caret is in it: the
          // border is `error`, and the label has to agree with it.
          '&.Mui-error, &.Mui-error.Mui-focused': { color: theme.palette.error.main },
          '&.Mui-disabled': { color: theme.palette.brand.disabledInk },
          '& .MuiFormLabel-asterisk': { color: theme.palette.error.main },
          /*
           * A field's label is chrome, not content. It is a real text node, so
           * any drag that crosses the field, a Select All, or a shift-click
           * past it takes the label into the selection and the browser paints
           * a block behind the label text — inside the notch of a focused
           * outline it reads as a rendering defect, and greys out to a pale
           * rectangle the moment the window loses focus (which is why it shows
           * up in screenshots). Nobody has ever wanted to copy a field label.
           *
           * Opting the label out is deliberately scoped to the label element:
           * `user-select` inherits, but the value lives in the `<input>`, a
           * sibling, so what the user typed stays selectable and copyable.
           * Same rule on the notched outline's `<legend>` above, which carries
           * a second, invisible copy of the same text.
           */
          ...NOT_SELECTABLE,
          '.MuiFormControl-root[dir="ltr"] > &': LTR_FIELD_CHROME,
        }),
        // MUI positions an outlined label absolutely and moves it with a
        // transform — inside the box at rest, into the notch when shrunk.
        // Both are undone: the label is a block in the FormControl's column,
        // above the box, the same in every state and at every size. It may
        // wrap: a long label in a narrow column says all of itself rather
        // than ending in an ellipsis nobody can expand.
        outlined: {
          position: 'relative',
          top: 'auto',
          left: 'auto',
          transform: 'none',
          maxWidth: '100%',
          whiteSpace: 'normal',
          overflow: 'visible',
          pointerEvents: 'auto',
          marginBottom: FIELD_LABEL.gap,
          '&.MuiInputLabel-shrink': { transform: 'none', maxWidth: '100%' },
          '&.MuiInputLabel-sizeSmall': { transform: 'none' },
        },
      },
    },
    // Helper and error lines: 12px, 6px under the box, faint when helping and
    // the danger tone when refusing.
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          marginInline: 0,
          marginTop: 6,
          fontSize: '0.75rem',
          lineHeight: 1.5,
          color: theme.palette.text.disabled,
          '&.Mui-error': { color: theme.palette.error.main },
          '.MuiFormControl-root[dir="ltr"] > &': LTR_FIELD_CHROME,
        }),
      },
    },
    MuiFormControlLabel: {
      // A checkbox's or a radio's caption is the control's own name, the same
      // chrome as a field label, and it highlights on the same stray drag.
      styleOverrides: {
        root: { gap: 4, marginInlineStart: -6 },
        label: ({ theme }) => ({
          fontSize: '0.9375rem',
          ...NOT_SELECTABLE,
          '&.Mui-disabled': { color: theme.palette.brand.disabledInk },
        }),
      },
    },
    // A radio group's or a checkbox group's heading (Components board's
    // "Gender"): 13px/500, muted, never moving.
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:not(.MuiInputLabel-root)': {
            fontSize: `${FIELD_LABEL.fontSize / 16}rem`,
            fontWeight: 500,
            color: theme.palette.text.secondary,
            marginBottom: 4,
            ...NOT_SELECTABLE,
          },
          '&:not(.MuiInputLabel-root).Mui-focused': { color: theme.palette.text.secondary },
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: radii.lg,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: popoverShadow(theme),
          marginTop: 6,
        }),
        list: {
          padding: 6,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          '& .MuiDivider-root': { marginBlock: '4px !important', marginInline: 6 },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: ({ theme }) => ({ borderRadius: radii.lg, boxShadow: popoverShadow(theme) }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: radii.md,
          minHeight: 38,
          paddingInline: 10,
          gap: 10,
          fontSize: '0.875rem',
          color: theme.palette.text.primary,
          '& .MuiListItemIcon-root': { minWidth: 0, color: theme.palette.text.secondary },
          '& .MuiListItemIcon-root .MuiSvgIcon-root': { fontSize: '1.125rem' },
          '&:hover': { backgroundColor: theme.palette.action.hover },
          // The one selected look: neutral fill, bold ink, icon in the primary.
          '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
            backgroundColor: theme.palette.brand.selected,
            color: theme.palette.text.primary,
            fontWeight: 600,
          },
          '&.Mui-selected .MuiListItemIcon-root': { color: theme.palette.primary.main },
          '&.Mui-focusVisible': { backgroundColor: theme.palette.action.hover },
        }),
      },
    },
    MuiListItemIcon: { styleOverrides: { root: { minWidth: 0 } } },
    // The inverse surface, 12px, 6px corners (Overlays board); a hairline
    // edge on black, where the inverse grey would otherwise melt into a panel.
    MuiTooltip: {
      defaultProps: { arrow: false, enterDelay: 300 },
      styleOverrides: {
        tooltip: ({ theme }) => ({
          borderRadius: radii.sm,
          fontSize: '0.75rem',
          fontWeight: 400,
          lineHeight: 1.5,
          padding: '6px 10px',
          backgroundColor: theme.palette.brand.inverseSurface,
          color: theme.palette.brand.inverseInk,
          border: `1px solid ${theme.palette.brand.inverseBorder}`,
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: radii.xl,
          boxShadow: overlayShadow(theme),
          border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 0,
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35, padding: '22px 22px 10px' },
      },
    },
    MuiDialogContent: { styleOverrides: { root: { paddingInline: 22 } } },
    MuiDialogContentText: {
      styleOverrides: {
        root: ({ theme }) => ({ fontSize: '0.875rem', lineHeight: 1.7, color: theme.palette.text.secondary }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        // `gap` spaces the buttons; MUI's own sibling margin would double it.
        // Dialog acts are 40px and 14px (Overlays board), a step down from a
        // page's 42/15: a dialog is a question, not a form.
        root: {
          padding: '18px 22px 22px',
          gap: 10,
          '& > :not(style) ~ :not(style)': { marginLeft: 0 },
          '& .MuiButton-root': { minHeight: 40, fontSize: '0.875rem', paddingInline: 16 },
        },
      },
    },
    /**
     * AppBar and Drawer are Paper underneath, so the global radius rounded the
     * topbar and the sidebar — which left a notch of the ground where the two met at
     * the top of the shell. Full-bleed chrome has square corners.
     */
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundImage: 'none', borderRadius: 0 },
        // A temporary drawer floats over the page; a permanent one is chrome.
        modal: ({ theme }) => ({ '& .MuiDrawer-paper': { boxShadow: overlayShadow(theme) } }),
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: { root: { borderRadius: 0 } },
    },
    /*
     * Tabs are text (Components and dark pass boards): 14px muted, 24px apart;
     * the selected one 600 in the ink with a 2px primary rule drawn *inside*
     * its own bottom edge (an inset shadow), over the list's hairline. MUI's
     * sliding indicator is not drawn — it is a second element that has to be
     * measured into place, and it would be the one thing on the page that
     * moves. A count rides beside the word in 12px faint Latin (`TabCount`).
     */
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 40 },
        indicator: { display: 'none' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 40,
          textTransform: 'none',
          fontWeight: 400,
          fontSize: '0.875rem',
          lineHeight: 1.5,
          paddingInline: 0,
          paddingBlock: 0,
          marginInlineEnd: 24,
          minWidth: 0,
          color: theme.palette.text.secondary,
          '&:hover': { color: theme.palette.text.primary },
          '&.Mui-selected': {
            color: theme.palette.text.primary,
            fontWeight: 600,
            boxShadow: `inset 0 -2px 0 ${theme.palette.primary.main}`,
          },
          '&.Mui-disabled': { color: theme.palette.brand.disabledInk },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({ borderColor: theme.palette.divider, paddingBlock: 10 }),
        head: ({ theme }) => ({
          fontWeight: 600,
          fontSize: '0.75rem',
          color: theme.palette.text.disabled,
          backgroundColor: theme.palette.brand.tableHead,
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.MuiTableRow-hover:hover': { backgroundColor: theme.palette.action.hover },
          '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: theme.palette.brand.selected },
        }),
      },
    },
    /*
     * Alerts (Components and dark pass boards): the tone's fill, a full 1px
     * border in the tone — never a side stripe — a 20px icon in the tone, an
     * optional 15px/600 title over a 14px body, and an optional link or button
     * at the inline end. Light writes title and body in deep steps of the
     * tone; dark writes the title in the tone and the body in the ink.
     */
    MuiAlert: {
      defaultProps: { variant: 'standard' },
      styleOverrides: {
        root: {
          borderRadius: radii.lg,
          alignItems: 'flex-start',
          padding: '14px 16px',
          gap: 12,
          borderWidth: 1,
          borderStyle: 'solid',
        },
        icon: { padding: 0, margin: 0, marginTop: 1, opacity: 1, fontSize: 20, '& .MuiSvgIcon-root': { fontSize: 20 } },
        message: { padding: 0, fontSize: '0.875rem', lineHeight: 1.6, minWidth: 0, flex: 1 },
        action: {
          padding: 0,
          margin: 0,
          marginInlineStart: 'auto',
          alignSelf: 'flex-start',
          '& .MuiButton-root': { minHeight: 30, marginBlock: -4 },
        },
        ...Object.fromEntries(
          (Object.keys(ALERT_TONES) as (keyof typeof ALERT_TONES)[]).map((severity) => [
            `standard${severity[0].toUpperCase()}${severity.slice(1)}`,
            ({ theme }: { theme: Theme }) => {
              const tone = alertTones[ALERT_TONES[severity]][theme.palette.mode];
              return {
                backgroundColor: tone.fill,
                borderColor: tone.border,
                color: tone.body,
                '& .MuiAlert-icon': { color: tone.icon },
                '& .MuiAlertTitle-root': { color: tone.title },
                // A link in an alert is the alert's own next step ("Complete
                // the details"): the title's colour, 600, underlined.
                '& .MuiAlert-message a, & .MuiAlert-action a, & .MuiAlert-action .MuiButton-text': {
                  color: tone.title,
                  fontWeight: 600,
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                },
              };
            },
          ]),
        ),
      },
    },
    MuiAlertTitle: {
      styleOverrides: { root: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.5, margin: '0 0 4px' } },
    },
    /*
     * A bar in flight — a route, a refetch, a save — is aqua, the signal
     * colour, on the neutral track; a bar that reports a real fraction is the
     * primary (Loaders board).
     */
    MuiLinearProgress: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: 3,
          borderRadius: radii.pill,
          backgroundColor: theme.palette.brand.selected,
        }),
        bar: ({ theme }) => ({ backgroundColor: theme.palette.brand.signal, borderRadius: radii.pill }),
        // A real fraction is a 6px indigo bar on the neutral track (Loaders
        // board, "Known progress") — never a fake percentage.
        determinate: ({ theme }) => ({
          height: 6,
          borderRadius: radii.sm,
          '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.primary.main, borderRadius: radii.sm },
        }),
      },
    },
    MuiAvatar: { styleOverrides: { root: { fontWeight: 600, fontSize: '0.8125rem' } } },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 600,
          fontSize: '0.6875rem',
          minWidth: 18,
          height: 18,
          padding: '0 5px',
        },
      },
    },
    // A still skeleton reads as a broken layout; it pulses, it does not
    // shimmer (Loaders board).
    MuiSkeleton: {
      defaultProps: { animation: 'pulse' },
      styleOverrides: {
        // The bars are the neutral selected grey (#EFEFF2 / #26262A), not
        // MUI's alpha of the ink, so a loading table reads the same in both
        // modes as the Loaders board draws it.
        root: ({ theme }) => ({ borderRadius: radii.xs, backgroundColor: theme.palette.brand.selected }),
        text: { transform: 'none', height: 10 },
        rounded: { borderRadius: radii.sm },
      },
    },
    MuiDivider: {
      styleOverrides: { root: ({ theme }) => ({ borderColor: theme.palette.divider }) },
    },
    /*
     * The switch (Components and dark pass boards): a 40 × 24 track and an
     * 18px thumb, 3px in. On is the primary track with the primary's label
     * colour as the thumb; off is the well with a strong hairline and a grey
     * thumb. The thumb travels by `--dir-scale`, so it moves toward the
     * inline end in both directions without a `theme.direction` check.
     */
    MuiSwitch: {
      styleOverrides: {
        root: { width: 40, height: 24, padding: 0, margin: 8, overflow: 'visible' },
        switchBase: ({ theme }) => ({
          padding: 3,
          color: theme.palette.brand.disabledInk,
          '&:hover': { backgroundColor: 'transparent' },
          '&.Mui-checked': {
            transform: 'translateX(calc(16px * var(--dir-scale)))',
            color: theme.palette.primary.contrastText,
          },
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: theme.palette.primary.main, opacity: 1, borderColor: 'transparent' },
          '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.5 },
          '&.Mui-disabled .MuiSwitch-thumb': { opacity: 0.6 },
          '&.Mui-focusVisible + .MuiSwitch-track': focusRing(theme),
        }),
        colorPrimary: ({ theme }) => ({
          '&.Mui-checked': { color: theme.palette.primary.contrastText },
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: theme.palette.primary.main },
        }),
        thumb: { width: 18, height: 18, boxShadow: 'none' },
        track: ({ theme }) => ({
          borderRadius: 12,
          opacity: 1,
          boxSizing: 'border-box',
          backgroundColor: theme.palette.brand.well,
          border: `1px solid ${theme.palette.brand.borderStrong}`,
        }),
      },
    },
    // The date pickers: the popover is a menu-like surface, the calendar's
    // selected day is the primary with its label colour, today is outlined.
    MuiPickersPopper: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: radii.lg,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: popoverShadow(theme),
          marginTop: 6,
        }),
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: '0.8125rem',
          '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
          '&:not(.Mui-selected)': { borderColor: theme.palette.brand.borderStrong },
        }),
      },
    },
    /*
     * The calendar header. MUI X gives it 24px of padding on the label side,
     * 12px on the arrows' side and two 40px buttons — the Material sizing,
     * which in a 320px calendar leaves the arrows crowded against the edge and
     * the month label. The arrows are brought onto the product's own icon
     * scale — a 20px glyph in a 32px target, the same as every input adornment
     * — and the header is padded symmetrically with logical properties, so
     * Arabic and English get the same header mirrored rather than two
     * different ones.
     */
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: { paddingInline: 12, marginBottom: 8 },
        label: { fontSize: '0.875rem', fontWeight: 600 },
        // The container hard-clips (`overflow: hidden`); a long Arabic month
        // ("تشرين الثاني") loses its last letters with no sign that it did.
        labelContainer: { maxWidth: '100%' },
        switchViewButton: { padding: 4, '& .MuiSvgIcon-root': { fontSize: '1.25rem' } },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: ({ theme }) => ({
          color: theme.palette.text.disabled,
          fontSize: '0.75rem',
        }),
      },
    },
    /*
     * The month arrows point the way the reader reads.
     *
     * MUI X does flip them itself — `PickersArrowSwitcher` renders the right
     * icon for "previous" when `useRtl()` is true — but `useRtl()` reads a
     * React context from `@mui/system`, and this install has two copies of
     * that package: `@mui/material` 6 resolves its own nested `@mui/system` 6,
     * while `@mui/x-date-pickers` resolves the hoisted `@mui/system` 7. Two
     * modules, two context objects, so the value our `ThemeProvider` publishes
     * never reaches the pickers and `isRtl` is always `false`. In Arabic that
     * left "next month" pointing right.
     *
     * Flipping the glyph is the repo's existing answer for a
     * direction-implying icon and needs no context at all: `--dir-scale` is
     * set from the document's `dir` in the baseline.
     */
    MuiPickersArrowSwitcher: {
      styleOverrides: {
        button: ({ theme }) => ({
          color: theme.palette.text.secondary,
          padding: 6,
          '& .MuiSvgIcon-root': { fontSize: '1.25rem', transform: 'scaleX(var(--dir-scale))' },
        }),
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          border: 'none',
          '--DataGrid-rowBorderColor': theme.palette.divider,
          // The grid paints its header and pinned areas with this; it defaults
          // to background.default, the page's grey. The header band is its
          // own token, one step off the panel.
          '--DataGrid-containerBackground': theme.palette.brand.tableHead,
          fontSize: '0.875rem',
          '& .MuiDataGrid-columnHeader': { backgroundColor: 'transparent' },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          // Header labels are the board's: 12px, semibold, faint — and the one
          // sorted column reads in the ink, beside its arrow.
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
            fontSize: '0.75rem',
            color: theme.palette.text.disabled,
          },
          '& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-columnHeaderTitle': {
            color: theme.palette.text.primary,
          },
          // A sortable column always shows that it is one: the faint double
          // arrow is drawn at rest, not only under the pointer.
          '& .MuiDataGrid-columnHeader--sortable .MuiDataGrid-iconButtonContainer': {
            visibility: 'visible',
            width: 'auto',
          },
          '& .MuiDataGrid-columnHeader--sortable:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortIcon':
            { opacity: 1, color: theme.palette.brand.borderStrong },
          '& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-sortIcon': { color: theme.palette.text.primary },
          '& .MuiDataGrid-iconButtonContainer .MuiIconButton-root': { padding: 2 },
          '& .MuiDataGrid-sortIcon': { fontSize: '1rem' },
          '& .MuiDataGrid-columnSeparator': { display: 'none' },
          '& .MuiDataGrid-cell': {
            borderBottomColor: theme.palette.divider,
            // Cells were left to stretch, so a two-line name, a chip and a
            // figure each settled at a different height and the row read as
            // broken. Everything centres on the row's midline.
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
          '& .MuiDataGrid-row:hover': { backgroundColor: theme.palette.brand.tableHead },
          '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: theme.palette.brand.selected,
          },
          // The row's actions show on hover or focus. The cell's contents fade,
          // not the cell, or its top rule goes with them and the row line
          // stops short of the table's edge.
          '& .row-actions > *': { opacity: 0 },
          '& .MuiDataGrid-row:hover .row-actions > *, & .MuiDataGrid-row:focus-within .row-actions > *': {
            opacity: 1,
          },
          '& .MuiDataGrid-overlay': { backgroundColor: 'transparent' },
          '& .MuiDataGrid-menuIconButton': { color: theme.palette.text.disabled },
        }),
      },
    },
  } as Components<Omit<Theme, 'components'>>;
}
